import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { get, post, put } from "../../redux/ApiHelper";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { AdminRole } from "../../enums/admin-roles";
import { successToast } from "../../utils/toast.util";
import { TicketInterface } from "../../interfaces/ticket.interface";
import { useSocket } from "../../context/SocketContext";

/** Matches global theme: yellow CTA, dark app chrome */
const sendButtonSx = {
  bgcolor: "var(--color-primary)",
  color: "#171717",
  fontWeight: 700,
  minWidth: 100,
  boxShadow: "none",
  "&:hover": {
    bgcolor: "#e6be06",
    color: "#171717",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    bgcolor: "rgba(255,255,255,0.10)",
    color: "rgba(255,255,255,0.45)",
  },
} as const;

const adminBubbleBg = "#1976d2";
const adminBubbleColor = "#fff";

type UserRoleTab = "parent" | "teacher";

const darkUi = {
  pageText: "rgba(255,255,255,0.86)",
  mutedText: "rgba(255,255,255,0.62)",
  faintText: "rgba(255,255,255,0.42)",
  border: "rgba(255,255,255,0.10)",
  // Base all surfaces on global app dark
  panel: "var(--primary-dark)",
  panel2: "#1f1f1f",
  panel3: "#262626",
  input: "#1b1b1b",
  listHover: "rgba(255,255,255,0.05)",
  selected: "rgba(250, 204, 21, 0.14)",
  selectedHover: "rgba(250, 204, 21, 0.20)",
} as const;

const textFieldDarkSx = {
  "& .MuiInputLabel-root": { color: darkUi.mutedText },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--color-primary)" },
  "& .MuiOutlinedInput-root": {
    bgcolor: darkUi.input,
    color: darkUi.pageText,
    "& fieldset": { borderColor: darkUi.border },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.18)" },
    "&.Mui-focused fieldset": { borderColor: "var(--color-primary)" },
    "&.Mui-disabled": {
      bgcolor: "rgba(255,255,255,0.06)",
      color: darkUi.faintText,
    },
  },
  "& .MuiInputBase-input::placeholder": { color: darkUi.faintText, opacity: 1 },
  "& .MuiFormHelperText-root": { color: darkUi.faintText },
} as const;


/** Normalize populated or raw admin ref from API JSON */
function getAssignedAdminIdFromTicket(ticket: TicketInterface | null): string | null {
  if (!ticket?.admin) return null;
  const a = ticket.admin as Record<string, unknown>;
  if (typeof ticket.admin === "string") return ticket.admin || null;
  if (typeof ticket.admin === "object" && ticket.admin !== null) {
    if (a._id != null) return String(a._id);
    if (a.id != null) return String(a.id);
    if (a.$oid != null) return String(a.$oid);
  }
  return null;
}

/** True when backend has an assignee (ObjectId or resolved profile). Populate("admin") was null for super-admin ids — API now resolves both. */
function ticketHasAssignee(ticket: TicketInterface | null): boolean {
  if (!ticket?.admin) return false;
  if (typeof ticket.admin === "string") return ticket.admin.length > 0;
  const a = ticket.admin as Record<string, unknown>;
  return !!(
    getAssignedAdminIdFromTicket(ticket) ||
    (typeof a.name === "string" && a.name.trim()) ||
    (typeof a.email === "string" && a.email.trim())
  );
}

function getAssignedAdminDisplayName(ticket: TicketInterface | null): string {
  if (!ticket?.admin) return "";
  if (typeof ticket.admin === "string") return "Another admin";
  const { name, email } = ticket.admin;
  const n = name?.trim();
  if (n) return n;
  const e = email?.trim();
  if (e) return e;
  return "Another admin";
}

const Chat = () => {
  const { signedAdmin } = useSelector((state: RootState) => state.admin);
  const { socket, socketConnected } = useSocket();
  const [tab, setTab] = useState<UserRoleTab>("parent");
  const [chatAccess, setChatAccess] = useState<{
    parent: boolean;
    teacher: boolean;
  } | null>(null);
  const [loadingPerm, setLoadingPerm] = useState(true);
  const [tickets, setTickets] = useState<TicketInterface[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<
    Array<{ _id?: string; message?: string; senderRole?: string; createdAt?: string }>
  >([]);
  const [ticket, setTicket] = useState<TicketInterface | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"open" | "closed" | "all">("open");
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const threadEndRef = useRef<HTMLDivElement>(null);

  const isSuper = signedAdmin.role === AdminRole.SUPER_ADMIN;
  const canParent = isSuper || !!chatAccess?.parent;
  const canTeacher = isSuper || !!chatAccess?.teacher;

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchDraft.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchDraft]);

  useEffect(() => {
    const loadPerm = async () => {
      if (!signedAdmin?.id) return;
      setLoadingPerm(true);
      try {
        const res = await get("/admin/ticket/permission", {
          adminRole: signedAdmin.role,
          adminId: signedAdmin.id,
        });
        const tp = res?.ticketPermission;
        setChatAccess(tp?.chatAccess ?? { parent: false, teacher: false });
      } catch {
        setChatAccess({ parent: false, teacher: false });
      } finally {
        setLoadingPerm(false);
      }
    };
    void loadPerm();
  }, [signedAdmin?.id, signedAdmin?.role]);

  useEffect(() => {
    if (canParent && !canTeacher) setTab("parent");
    if (!canParent && canTeacher) setTab("teacher");
  }, [canParent, canTeacher]);

  const fetchInbox = useCallback(async () => {
    setListError(null);
    if (tab === "parent" && !canParent) {
      setTickets([]);
      return;
    }
    if (tab === "teacher" && !canTeacher) {
      setTickets([]);
      return;
    }
    setListLoading(true);
    try {
      const params: Record<string, string> = {
        userRole: tab,
        status: statusFilter,
      };
      if (search) params.search = search;
      const res = await get("/admin/ticket/inbox", params);
      setTickets(res?.tickets || []);
    } catch {
      setTickets([]);
      setListError("Could not load inbox. Check your ticket permissions or try again.");
    } finally {
      setListLoading(false);
    }
  }, [tab, statusFilter, search, canParent, canTeacher]);

  useEffect(() => {
    void fetchInbox();
  }, [fetchInbox]);

  /** Subscribe to support ticket realtime updates for the active queue tab */
  useEffect(() => {
    if (!socket || !socketConnected) return;
    if (tab === "parent" && !canParent) return;
    if (tab === "teacher" && !canTeacher) return;
    socket.emit("join-support-inbox", { userRole: tab });
  }, [socket, socketConnected, tab, canParent, canTeacher]);

  const loadTicketDetail = useCallback(
    async (id: string, options?: { showSpinner?: boolean }) => {
      const showSpinner = options?.showSpinner !== false;
      if (showSpinner) setDetailLoading(true);
      try {
        const res = await get(`/admin/ticket/${id}`);
        setTicket(res.ticket);
        setMessages(res.messages || []);
      } catch {
        if (showSpinner) {
          setTicket(null);
          setMessages([]);
        }
      } finally {
        if (showSpinner) setDetailLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!socket || !socketConnected) return;
    const onTicketUpdate = (data: { ticketId: string; userRole: string }) => {
      if (data.userRole === tab) {
        void fetchInbox();
      }
      if (selectedId && data.ticketId === selectedId) {
        void loadTicketDetail(selectedId, { showSpinner: false });
      }
    };
    socket.on("ticket-update", onTicketUpdate);
    return () => {
      socket.off("ticket-update", onTicketUpdate);
    };
  }, [
    socket,
    socketConnected,
    tab,
    selectedId,
    fetchInbox,
    loadTicketDetail,
  ]);

  useEffect(() => {
    setCloseConfirmOpen(false);
    if (!selectedId) {
      setTicket(null);
      setMessages([]);
      return;
    }
    setTicket(null);
    setMessages([]);
    void loadTicketDetail(selectedId, { showSpinner: true });
  }, [selectedId, loadTicketDetail]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const adminIdStr = String(signedAdmin.id ?? "");
  const assignedAdminId = getAssignedAdminIdFromTicket(ticket);
  const assignedAdminName = getAssignedAdminDisplayName(ticket);
  const hasAssignee = ticketHasAssignee(ticket);

  const assignedToMe = !!assignedAdminId && assignedAdminId === adminIdStr;
  const assignedToOther =
    !!assignedAdminId && !assignedToMe && ticket?.status === "open";

  const canReply = !!ticket && ticket.status === "open";

  const canClose =
    !!ticket &&
    ticket.status === "open" &&
    (isSuper || (!!assignedAdminId && assignedToMe));

  const send = async () => {
    if (!selectedId || !draft.trim()) return;
    setSending(true);
    try {
      const res = await post(`/admin/ticket/${selectedId}/message`, {
        message: draft.trim(),
      });
      setTicket(res.ticket);
      setMessages(res.messages || []);
      setDraft("");
      successToast("Message sent");
      void fetchInbox();
    } catch (err) {
      // ApiHelper already shows errorToast; avoid unhandled rejection (CRA error overlay)
      console.error("[Support inbox] Failed to send message:", err);
      if (selectedId) void loadTicketDetail(selectedId, { showSpinner: false });
      void fetchInbox();
    } finally {
      setSending(false);
    }
  };

  const closeTicket = async () => {
    if (!selectedId) return;
    setCloseConfirmOpen(false);
    setClosing(true);
    try {
      const res = await put(`/admin/ticket/${selectedId}/close`, {});
      setTicket(res.ticket);
      successToast("Ticket closed");
      void fetchInbox();
    } catch (err) {
      console.error("[Support inbox] Failed to close ticket:", err);
      if (selectedId) void loadTicketDetail(selectedId, { showSpinner: false });
      void fetchInbox();
    } finally {
      setClosing(false);
    }
  };

  const userLabel = (t: TicketInterface) => {
    if (t.userRole === "teacher") {
      return t.teacher?.name || t.teacher?.email || "Teacher";
    }
    return t.parent?.name || t.parent?.email || "Parent";
  };

  if (loadingPerm) {
    return (
      <div className="chat-support table-container min-h-0 flex-1">
        <Box className="flex justify-center items-center flex-1 min-h-[200px]">
          <CircularProgress sx={{ color: "var(--color-primary)" }} />
        </Box>
      </div>
    );
  }

  if (!canParent && !canTeacher) {
    return (
      <div className="chat-support table-container min-h-0 flex-1">
        <div className="title">Support inbox</div>
        <Alert
          severity="info"
          sx={{
            bgcolor: "rgba(255,255,255,0.08)",
            color: darkUi.pageText,
            border: `1px solid ${darkUi.border}`,
            "& .MuiAlert-icon": { color: "var(--color-primary)" },
          }}
        >
          You do not have support inbox access. Ask a super-admin to grant Parent and/or Teacher chat
          access in Admin List.
        </Alert>
      </div>
    );
  }

  return (
    <div className="chat-support table-container min-h-0 flex-1">
      <div className="title">Support inbox</div>
      <Typography
        variant="body2"
        sx={{ color: darkUi.mutedText, mb: 2, maxWidth: 720 }}
      >
        Reply to parents and teachers. Your first message assigns the ticket to you until it is
        closed.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: darkUi.panel,
          borderRadius: 2,
          overflow: "hidden",
          border: `1px solid ${darkUi.border}`,
          boxShadow: "0 10px 40px rgba(0,0,0,0.55)",
          color: darkUi.pageText,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setSelectedId(null);
          }}
          TabIndicatorProps={{
            sx: {
              height: 3,
              bgcolor: "var(--color-primary)",
            },
          }}
          sx={{
            borderBottom: 1,
            borderColor: darkUi.border,
            px: 1,
            bgcolor: darkUi.panel2,
            "& .MuiTab-root": {
              textTransform: "uppercase",
              fontWeight: 700,
              fontSize: "0.8rem",
              letterSpacing: "0.04em",
              color: darkUi.mutedText,
            },
            "& .Mui-selected": {
              color: darkUi.pageText,
            },
          }}
        >
          <Tab label="Parents" value="parent" disabled={!canParent} />
          <Tab label="Teachers" value="teacher" disabled={!canTeacher} />
        </Tabs>

        <Box
          sx={{
            display: "flex",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              width: 340,
              flexShrink: 0,
              borderRight: 1,
              borderColor: darkUi.border,
              display: "flex",
              flexDirection: "column",
              bgcolor: darkUi.panel2,
            }}
          >
            <Box sx={{ p: 1.5, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Typography variant="caption" sx={{ color: darkUi.mutedText, alignSelf: "center" }}>
                Status
              </Typography>
              <Box
                component="select"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as typeof statusFilter)
                }
                sx={{
                  minWidth: 120,
                  p: 1,
                  borderRadius: 1,
                  border: 1,
                  borderColor: darkUi.border,
                  bgcolor: darkUi.input,
                  color: darkUi.pageText,
                  outline: "none",
                  "&:focus": { borderColor: "var(--color-primary)" },
                  "& option": { color: "#111827" },
                }}
              >
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="all">All</option>
              </Box>
              <TextField
                size="small"
                label="Search"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                fullWidth
                placeholder="Name or subject"
                sx={textFieldDarkSx}
              />
            </Box>
            {listError && (
              <Alert
                severity="warning"
                sx={{
                  mx: 1,
                  mb: 1,
                  bgcolor: "rgba(245, 158, 11, 0.12)",
                  color: darkUi.pageText,
                  border: "1px solid rgba(245, 158, 11, 0.22)",
                  "& .MuiAlert-icon": { color: "rgba(245, 158, 11, 0.9)" },
                }}
              >
                {listError}
              </Alert>
            )}
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {listLoading ? (
                <Box className="flex justify-center py-6">
                  <CircularProgress size={28} sx={{ color: "var(--color-primary)" }} />
                </Box>
              ) : tickets.length === 0 ? (
                <Typography variant="body2" sx={{ p: 2, color: darkUi.mutedText }}>
                  No tickets in this queue.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {tickets.map((t) => {
                    const assignedIdForRow = getAssignedAdminIdFromTicket(t);
                    const assignedToMeRow =
                      !!assignedIdForRow && assignedIdForRow === adminIdStr;

                    return (
                      <ListItemButton
                        key={t._id}
                        selected={selectedId === t._id}
                        onClick={() => setSelectedId(t._id)}
                        alignItems="flex-start"
                        sx={{
                          color: darkUi.pageText,
                          "&:hover": { bgcolor: darkUi.listHover },
                          "&.Mui-selected": {
                            bgcolor: darkUi.selected,
                          },
                          "&.Mui-selected:hover": {
                            bgcolor: darkUi.selectedHover,
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box className="flex items-center gap-1 flex-wrap">
                              <Typography variant="subtitle2" component="span" sx={{ color: darkUi.pageText }}>
                                {userLabel(t)}
                              </Typography>
                              <Chip
                                size="small"
                                label={t.status === "open" ? "Open" : "Closed"}
                                color={t.status === "open" ? "success" : "default"}
                              />
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" display="block" noWrap sx={{ color: darkUi.mutedText }}>
                                {t.subject || t.lastMessage?.message || "—"}
                              </Typography>
                              {assignedToMeRow && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{ color: "var(--color-primary)" }}
                                >
                                  Assigned to you
                                </Typography>
                              )}
                              <Typography variant="caption" sx={{ color: darkUi.faintText }}>
                                {t.updatedAt
                                  ? new Date(t.updatedAt).toLocaleString()
                                  : ""}
                              </Typography>
                            </>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>

          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            {!selectedId ? (
              <Box className="flex flex-1 items-center justify-center p-4" sx={{ minHeight: 0 }}>
                <Typography sx={{ color: darkUi.mutedText }}>
                  Select a ticket to view the conversation.
                </Typography>
              </Box>
            ) : detailLoading && !ticket ? (
              <Box className="flex flex-1 items-center justify-center" sx={{ minHeight: 0 }}>
                <CircularProgress sx={{ color: "var(--color-primary)" }} />
              </Box>
            ) : (
              <>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: darkUi.border, bgcolor: darkUi.panel2 }}>
                  <Box className="flex flex-wrap items-center gap-2 justify-between">
                    <Typography variant="h6" sx={{ color: darkUi.pageText }}>
                      {ticket ? userLabel(ticket) : ""}
                    </Typography>
                    <Box className="flex gap-1 flex-wrap">
                      {ticket?.status === "open" ? (
                        <Chip label="Open" color="success" size="small" />
                      ) : (
                        <Chip label="Closed" size="small" />
                      )}
                      {!hasAssignee && ticket?.status === "open" && (
                        <Chip label="Unassigned — your reply assigns you" color="warning" size="small" />
                      )}
                      {hasAssignee && (
                        <Chip
                          label={
                            assignedToMe
                              ? "Assigned to you"
                              : `Assigned: ${assignedAdminName || "Support"} — your reply takes over`
                          }
                          size="small"
                          variant="outlined"
                          sx={{
                            color: darkUi.pageText,
                            borderColor: "rgba(255,255,255,0.22)",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  {assignedToOther && (
                    <Alert
                      severity="info"
                      sx={{
                        mt: 1.5,
                        bgcolor: "rgba(59, 130, 246, 0.12)",
                        color: darkUi.pageText,
                        border: "1px solid rgba(59, 130, 246, 0.22)",
                      }}
                      icon={false}
                    >
                      <Typography variant="body2" component="span">
                        Currently assigned to{" "}
                        <strong>{assignedAdminName}</strong>. Sending a reply will
                        assign this ticket to you.
                      </Typography>
                    </Alert>
                  )}
                  {ticket?.subject && (
                    <Typography variant="body2" className="mt-1" sx={{ color: darkUi.mutedText }}>
                      {ticket.subject}
                    </Typography>
                  )}
                  <Box className="flex gap-2 mt-2">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={!canClose || closing}
                      onClick={() => setCloseConfirmOpen(true)}
                      sx={{ textTransform: "uppercase", fontWeight: 700, fontSize: "0.75rem" }}
                    >
                      Close ticket
                    </Button>
                  </Box>
                  <Dialog
                    open={closeConfirmOpen}
                    onClose={() => !closing && setCloseConfirmOpen(false)}
                    aria-labelledby="close-ticket-dialog-title"
                    aria-describedby="close-ticket-dialog-description"
                    PaperProps={{
                      sx: {
                        bgcolor: darkUi.panel2,
                        color: darkUi.pageText,
                        border: `1px solid ${darkUi.border}`,
                      },
                    }}
                  >
                    <DialogTitle id="close-ticket-dialog-title">Close this ticket?</DialogTitle>
                    <DialogContent>
                      <DialogContentText id="close-ticket-dialog-description" sx={{ color: darkUi.mutedText }}>
                        The parent or teacher will need to start a new ticket for further support.
                        This cannot be undone from here.
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                      <Button onClick={() => setCloseConfirmOpen(false)} disabled={closing}>
                        Cancel
                      </Button>
                      <Button
                        onClick={() => void closeTicket()}
                        color="error"
                        variant="contained"
                        disabled={closing}
                        autoFocus
                      >
                        {closing ? "Closing…" : "Close ticket"}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Box>

                <Box sx={{ flex: 1, overflow: "auto", p: 2, bgcolor: darkUi.panel, minHeight: 0 }}>
                  {messages.map((m) => {
                    const fromTeam =
                      m.senderRole === "admin" || m.senderRole === "super-admin";
                    return (
                      <Box
                        key={m._id || `${m.createdAt}-${m.message}`}
                        sx={{
                          display: "flex",
                          justifyContent: fromTeam ? "flex-end" : "flex-start",
                          mb: 1.5,
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            maxWidth: "78%",
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: fromTeam ? adminBubbleBg : darkUi.panel3,
                            color: fromTeam ? adminBubbleColor : darkUi.pageText,
                            border: fromTeam ? 0 : 1,
                            borderColor: darkUi.border,
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {m.message}
                          </Typography>
                          {m.createdAt && (
                            <Typography
                              variant="caption"
                              sx={{ opacity: 0.75, display: "block", mt: 0.5 }}
                            >
                              {new Date(m.createdAt).toLocaleString()}
                            </Typography>
                          )}
                        </Paper>
                      </Box>
                    );
                  })}
                  <div ref={threadEndRef} />
                </Box>

                <Divider />
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-end",
                    bgcolor: darkUi.panel2,
                    flexShrink: 0,
                    borderTop: `1px solid ${darkUi.border}`,
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder={
                      canReply
                        ? assignedToOther
                          ? `Reply to take over from ${assignedAdminName || "another admin"}…`
                          : "Type your reply…"
                        : ticket?.status === "closed"
                          ? "Ticket is closed"
                          : "Cannot reply"
                    }
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    disabled={!canReply || sending}
                    sx={textFieldDarkSx}
                  />
                  <Button
                    variant="contained"
                    disabled={!canReply || sending || !draft.trim()}
                    onClick={() => void send()}
                    sx={sendButtonSx}
                  >
                    {sending ? "Sending…" : "Send"}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Paper>
    </div>
  );
};

export default Chat;
